import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Instagram, ShoppingBag, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ImageCarousel } from "@/components/ImageCarousel";
import api from "@/lib/api";
import { openPaymentModal } from "@/lib/razorpay";
import { getVisitorToken, getSource, trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutPage() {
  const { packId } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [igHandle, setIgHandle] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get(`/packs/${packId}`);
        setPack(data);
      } catch {
        toast.error("Pack not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [packId, navigate]);

  const effectivePrice = pack
    ? pack.discounted_price ?? pack.price
    : 0;

  function validateEmail(e) {
    const v = e.trim();
    if (!v) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Enter a valid email";
    return "";
  }

  function handleContinue() {
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }
    setEmailError("");
    setConfirming(true);
  }

  async function handlePay() {
    setProcessing(true);
    try {
      const { data: orderData } = await api.post("/checkout", {
        pack_id: packId,
        email: email.trim(),
        instagram_handle: igHandle.trim() || null,
        visitor_token: getVisitorToken(),
        source: getSource(),
      });

      trackEvent("checkout_started", packId);

      openPaymentModal({
        orderData,
        onSuccess: (result) => {
          navigate(`/order/${result.order_id}?status=paid`);
        },
        onError: (msg) => {
          if (orderData.order_id) {
            navigate(`/order/${orderData.order_id}?status=failed`);
          } else {
            toast.error(msg || "Payment failed");
            setProcessing(false);
            setConfirming(false);
          }
        },
        onDismiss: () => {
          setProcessing(false);
          setConfirming(false);
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create order");
      setProcessing(false);
      setConfirming(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-coral" />
      </div>
    );
  }

  if (!pack) return null;

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-bold text-lg">Checkout</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Order Summary */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 flex gap-4 mb-6"
        >
          <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-secondary">
            {pack.images?.[0] ? (
              <img
                src={`${API_URL}/api/images/${pack.images[0].storage_path}`}
                alt="" className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">No img</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading font-bold text-base text-slate-800 truncate">{pack.title}</h2>
            {pack.page_count && (
              <p className="text-xs text-slate-500 mt-0.5">{pack.page_count} pages</p>
            )}
            <div className="flex items-baseline gap-2 mt-2">
              {pack.discounted_price != null ? (
                <>
                  <span className="font-heading font-bold text-lg text-coral">₹{pack.discounted_price}</span>
                  <span className="text-sm text-slate-400 line-through">₹{pack.price}</span>
                </>
              ) : (
                <span className="font-heading font-bold text-lg text-slate-800">₹{pack.price}</span>
              )}
            </div>
          </div>
        </motion.div>

        {!confirming ? (
          /* Email Form */
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block flex items-center gap-1.5">
                <Mail className="w-4 h-4" /> Email for your PDF *
              </label>
              <Input
                type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                placeholder="your@email.com"
                className={`rounded-xl h-12 text-base ${emailError ? "border-red-400" : ""}`}
                data-testid="checkout-email-input"
              />
              {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
              <p className="text-xs text-slate-400 mt-1">We'll send your printable PDF to this email</p>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block flex items-center gap-1.5">
                <Instagram className="w-4 h-4" /> Instagram handle (optional)
              </label>
              <Input
                value={igHandle}
                onChange={(e) => setIgHandle(e.target.value)}
                placeholder="@yourhandle"
                className="rounded-xl h-12 text-base"
                data-testid="checkout-ig-input"
              />
            </div>

            <Button
              onClick={handleContinue}
              className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-14 text-base font-bold"
              data-testid="checkout-continue-btn"
            >
              Continue to Payment
            </Button>
          </motion.div>
        ) : (
          /* Confirmation */
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
            <div className="bg-blue-50/70 rounded-2xl p-5 text-center">
              <Mail className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-sm text-slate-700 font-body">
                We'll send your PDF to
              </p>
              <p className="font-bold text-slate-800 text-lg mt-1">{email}</p>
              <Button
                variant="ghost" size="sm"
                onClick={() => setConfirming(false)}
                className="mt-2 text-coral text-xs"
              >
                Edit email
              </Button>
            </div>

            <div className="bg-white rounded-2xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Product</span>
                <span className="font-semibold text-slate-800">{pack.title}</span>
              </div>
              {pack.page_count && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Pages</span>
                  <span className="text-slate-800">{pack.page_count}</span>
                </div>
              )}
              <div className="flex justify-between text-sm border-t border-border pt-2 mt-2">
                <span className="font-semibold text-slate-700">Total</span>
                <span className="font-heading font-bold text-lg text-coral">₹{effectivePrice}</span>
              </div>
            </div>

            <Button
              onClick={handlePay}
              disabled={processing}
              className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-14 text-lg font-bold shadow-md shadow-coral/20"
              data-testid="checkout-pay-btn"
            >
              {processing ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><ShoppingBag className="w-5 h-5 mr-2" /> Pay ₹{effectivePrice} securely</>
              )}
            </Button>
            <p className="text-center text-xs text-slate-400">
              UPI · Cards · Netbanking · via Razorpay
            </p>
          </motion.div>
        )}
      </main>
    </div>
  );
}
